import { HTTPService } from './httpService';
import { unwrapTemplatesList, normalizeTemplateOverrideRow } from '../util/serviceOverrideUsage';

const API = new HTTPService();

/**
 * Per-template price overrides.
 *
 * Templates live on the RAW endpoints (`/templates/...`), not under `/api` and
 * not in `servicesApiV2` — match that neighbourhood.
 *
 * `specialist_id` is not decoration: (template_id, specialist_id) is a
 * COMPOSITE key throughout the template subsystem, so every payload carries it.
 */

const withSpecialist = (specialistId, prices) =>
  (prices || []).map((p) => ({ ...p, specialist_id: Number(specialistId) }));

/** All override rows for one template. Absent row = inherit. */
export const getTemplatePrices = async (specialistId, templateId) => {
  const { data } = await API.get(`/templates/${specialistId}/${templateId}/prices`);
  return data?.data || data || [];
};

/** Bulk upsert. The grid saves a screenful; per-cell writes would be dozens of calls. */
export const saveTemplatePrices = async (specialistId, templateId, prices) => {
  if (!prices || !prices.length) return { success: true, skipped: true };
  const { data } = await API.put(`/templates/${specialistId}/${templateId}/prices`, {
    prices: withSpecialist(specialistId, prices),
  });
  return data?.data || data;
};

/** Bulk clear. Deleting the row is how a cell returns to inheriting. */
export const deleteTemplatePrices = async (specialistId, templateId, prices) => {
  if (!prices || !prices.length) return { success: true, skipped: true };
  const { data } = await API.delete(`/templates/${specialistId}/${templateId}/prices`, {
    data: { prices: withSpecialist(specialistId, prices) },
  });
  return data?.data || data;
};

/**
 * Which of the specialist's templates hold an absolute-price override for one
 * service — the `ServiceEditView` seam. A base-price edit never reaches a
 * dealership on an overriding template, so this is the read that tells an
 * installer whether the number they're about to change is the one in play.
 *
 * The payload has NOT been observed from this repo (server prompt reply
 * only), so every field is read defensively: `template_name ?? name`,
 * `cells` tolerated absent/empty/malformed, and the array unwrapped from
 * whichever depth this repo's endpoints tend to use (`data`, `data.templates`,
 * `data.data`, `data.data.templates`) — see `unwrapTemplatesList` in
 * `util/serviceOverrideUsage.js` for the exact rules, including which shapes
 * throw rather than resolve to an empty list.
 *
 * Deliberately does NOT catch — a request failure, OR a 200 whose body
 * `unwrapTemplatesList` can't recognize, must propagate so the caller renders
 * "could not check" rather than silently rendering the same empty state as
 * "no overrides", which would claim a price change is safe when it may not
 * be. TEMP: the raw-response log below stays until that shape is confirmed —
 * see CLAUDE.md's "Temporary debug logging" table.
 */
export const getServiceOverrideUsage = async (specialistId, masterServiceId) => {
  const { data } = await API.get(`/templates/${specialistId}/service/${masterServiceId}/overrides`);
  // Debug with data — inspect the raw payload once rather than guess at its shape.
  console.log('[ServiceOverrides] raw:', JSON.stringify(data, null, 2));

  // Throws on anything it can't recognize as a templates list — including a
  // well-formed 200 with an envelope this code doesn't understand. Not
  // caught here on purpose (see doc comment above).
  const list = unwrapTemplatesList(data);

  return list.map(normalizeTemplateOverrideRow);
};

const templatePricing = {
  getTemplatePrices, saveTemplatePrices, deleteTemplatePrices, getServiceOverrideUsage,
};
export default templatePricing;
